import React, { useEffect, useState } from 'react';
import {
  TextField,
  IconButton,
  InputAdornment,
  Button,
  Box,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Skeleton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Upload,
  Visibility as ViewIcon,
  Edit,
  MoreVert,
  Delete,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

const URL = process.env.REACT_APP_API_ENDPOINT;

const SettingPage = () => {
  const [activeTab, setActiveTab] = useState(0); // Password tab is default
  
  // Password/Security state
  const [values, setValues] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
    showOldPassword: false,
    showNewPassword: false,
    showConfirmPassword: false,
  });
  const [error, setError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Theme and Font state
  const [themeFile, setThemeFile] = useState(null);
  const [fontFile, setFontFile] = useState(null);
  const [currentTheme, setCurrentTheme] = useState(null);
  const [currentFont, setCurrentFont] = useState(null);
  const [themePreviewOpen, setThemePreviewOpen] = useState(false);
  const [fontPreviewOpen, setFontPreviewOpen] = useState(false);
  const [appearanceLoading, setAppearanceLoading] = useState(false);
  const [fontMenuAnchor, setFontMenuAnchor] = useState(null);
  const [selectedFont, setSelectedFont] = useState(null);
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(false);

  const passwordValidation = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  const handleChange = (prop) => (event) => {
    const value = event.target.value;
    setValues({ ...values, [prop]: value });

    if (prop === 'newPassword') {
      setPasswordError(
        passwordValidation(value)
          ? null
          : 'Password must contain at least 8 characters, including uppercase, lowercase, number, and special character.'
      );
    }
  };

  const toggleVisibility = (prop) => {
    setValues({ ...values, [prop]: !values[prop] });
  };

  const isButtonDisabled =
    !values.oldPassword ||
    !values.newPassword ||
    !values.confirmPassword ||
    values.newPassword !== values.confirmPassword ||
    passwordError;

  const handleSubmit = async (e) => {
    const accessToken = localStorage.getItem('accessToken');
    e.preventDefault();
    setError(null);
    setLoading(true); 

    const formdata = new FormData();
    formdata.append('OldPassword', values.oldPassword);
    formdata.append('NewPassword', values.newPassword);

    if (!passwordValidation(values.newPassword)) {
      setPasswordError('Password does not meet security requirements.');
      return;
    }

    try {
      const response = await fetch(`${URL}Register/ResetPassword`, {
        method: 'POST',
        body: formdata,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.status === 200) {
        setIsSuccess(true);
        setValues({
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
          showOldPassword: false,
          showNewPassword: false,
          showConfirmPassword: false,
        });
      } else {
        const data = await response.json();
        setError(data.detail || 'Something went wrong!');
      }
    } catch (err) {
      setError('Network error, please try again later.');
    } finally {
      setLoading(false); // Stop loading
    }
  };
  
  // Theme and Font handlers
  const handleThemeUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setThemeFile(file);
      toast.success('Theme file selected. Click "Upload Theme" to save.');
    }
  };

  const handleFontUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFontFile(file);
      toast.success('Font file selected. Click "Upload Font" to save.');
    }
  };

  const uploadTheme = async () => {
    if (!themeFile) {
      toast.error('Please select a theme file first');
      return;
    }
    setAppearanceLoading(true);
    try {
      const formData = new FormData();
      formData.append('template_name', themeFile.name.replace(/\.[^/.]+$/, '')); // Template name without extension
      formData.append('template_file', themeFile);
      formData.append('is_timing_available', 0); // Default to 0
      const accessToken = localStorage.getItem('accessToken');
      
      const response = await fetch(`${URL}Font/create-template`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        toast.success('Theme uploaded successfully');
        setThemeFile(null);
        // Fetch themes list
        fetchCurrentTheme();
      } else {
        toast.error('Failed to upload theme');
      }
    } catch (err) {
      toast.error('Error uploading theme');
    } finally {
      setAppearanceLoading(false);
    }
  };

  const uploadFont = async () => {
    if (!fontFile) {
      toast.error('Please select a font file first');
      return;
    }
    setAppearanceLoading(true);
    try {
      const formData = new FormData();
      formData.append('font_name', fontFile.name.replace(/\.[^/.]+$/, '')); // Font name without extension
      formData.append('font_file', fontFile);
      const accessToken = localStorage.getItem('accessToken');
      
      const response = await fetch(`${URL}Font/create-font`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        toast.success('Font uploaded successfully');
        setFontFile(null);
        // Fetch current font
        fetchCurrentFont();
      } else {
        toast.error('Failed to upload font');
      }
    } catch (err) {
      toast.error('Error uploading font');
    } finally {
      setAppearanceLoading(false);
    }
  };

  const fetchCurrentTheme = async () => {
    setThemeLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch(`${URL}Font/list-templates`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentTheme(data);
      }
    } catch (err) {
      console.error('Error fetching theme:', err);
    } finally {
      setThemeLoading(false);
    }
  };

  const fetchCurrentFont = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch(`${URL}Font/list-fonts`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentFont(data);
      }
    } catch (err) {
      console.error('Error fetching font:', err);
    }
  };

  const updateTheme = async (themeId, themeName) => {
    if (!themeFile) {
      toast.error('Please select a new theme file first');
      return;
    }
    setAppearanceLoading(true);
    try {
      const formData = new FormData();
      formData.append('id', themeId);
      formData.append('template_name', themeName);
      formData.append('template_file', themeFile);
      formData.append('is_timing_available', 0);
      const accessToken = localStorage.getItem('accessToken');
      
      const response = await fetch(`${URL}Font/update-template`, {
        method: 'PUT',
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        toast.success('Theme updated successfully');
        setThemeFile(null);
        setSelectedTheme(null);
        fetchCurrentTheme();
      } else {
        toast.error('Failed to update theme');
      }
    } catch (err) {
      toast.error('Error updating theme');
    } finally {
      setAppearanceLoading(false);
    }
  };

  const deleteTheme = async (themeId) => {
    setAppearanceLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch(`${URL}Font/delete-template`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: JSON.stringify({ id: themeId }),
      });

      if (response.ok) {
        toast.success('Theme deleted successfully');
        fetchCurrentTheme();
      } else {
        toast.error('Failed to delete theme');
      }
    } catch (err) {
      toast.error('Error deleting theme');
    } finally {
      setAppearanceLoading(false);
      setThemeMenuAnchor(null);
      setSelectedTheme(null);
    }
  };

  const updateFont = async (fontId, fontName) => {
    if (!fontFile) {
      toast.error('Please select a new font file first');
      return;
    }
    setAppearanceLoading(true);
    try {
      const formData = new FormData();
      formData.append('id', fontId);
      formData.append('font_name', fontName);
      formData.append('font_file', fontFile);
      const accessToken = localStorage.getItem('accessToken');
      
      const response = await fetch(`${URL}Font/update-font`, {
        method: 'PUT',
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        toast.success('Font updated successfully');
        setFontFile(null);
        setSelectedFont(null);
        fetchCurrentFont();
      } else {
        toast.error('Failed to update font');
      }
    } catch (err) {
      toast.error('Error updating font');
    } finally {
      setAppearanceLoading(false);
    }
  };

  const deleteFont = async (fontId) => {
    setAppearanceLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch(`${URL}Font/delete-font`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: JSON.stringify({ id: fontId }),
      });

      if (response.ok) {
        toast.success('Font deleted successfully');
        fetchCurrentFont();
      } else {
        toast.error('Failed to delete font');
      }
    } catch (err) {
      toast.error('Error deleting font');
    } finally {
      setAppearanceLoading(false);
      setFontMenuAnchor(null);
      setSelectedFont(null);
    }
  };

  const handleFontMenuOpen = (event, font) => {
    setFontMenuAnchor(event.currentTarget);
    setSelectedFont(font);
  };

  const handleFontMenuClose = () => {
    setFontMenuAnchor(null);
  };

  const handleThemeMenuOpen = (event, theme) => {
    setThemeMenuAnchor(event.currentTarget);
    setSelectedTheme(theme);
  };

  const handleThemeMenuClose = () => {
    setThemeMenuAnchor(null);
  };


  useEffect(() => {
    let timer;
    if (isSuccess) {
      timer = setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    }

    return () => clearTimeout(timer); 
  }, [isSuccess]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchCurrentTheme();
    } else if (activeTab === 2) {
      fetchCurrentFont();
    }
  }, [activeTab]);

  const renderSecurityTab = () => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        Password
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Update your password to keep your account secure.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {isSuccess && <Alert severity="success" sx={{ mb: 2 }}>Password changed successfully</Alert>}

      <TextField
        fullWidth
        label="Old Password"
        type={values.showOldPassword ? 'text' : 'password'}
        variant="outlined"
        margin="normal"
        value={values.oldPassword}
        onChange={handleChange('oldPassword')}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => toggleVisibility('showOldPassword')}>
                {values.showOldPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        label="New Password"
        type={values.showNewPassword ? 'text' : 'password'}
        variant="outlined"
        margin="normal"
        value={values.newPassword}
        onChange={handleChange('newPassword')}
        error={!!passwordError}
        helperText={passwordError}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => toggleVisibility('showNewPassword')}>
                {values.showNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        label="Confirm Password"
        type={values.showConfirmPassword ? 'text' : 'password'}
        variant="outlined"
        margin="normal"
        value={values.confirmPassword}
        onChange={handleChange('confirmPassword')}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => toggleVisibility('showConfirmPassword')}>
                {values.showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        fullWidth
        size="large"
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
        disabled={isButtonDisabled}
        onClick={handleSubmit}
        startIcon={loading ? <CircularProgress size={24} color='grey'/> : null}
      >
        {loading ? 'Updating...' : 'Update Password'}
      </Button>
    </Box>
  );

  const handleThemeDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      if (!file.type.includes('png') && !file.name.endsWith('.png')) {
        toast.error('Only PNG files are allowed for theme');
        return;
      }
      setThemeFile(file);
      toast.success('Theme file selected. Click "Save Theme" to upload.');
    }
  };

  const handleThemeDragOver = (e) => {
    e.preventDefault();
  };

  const renderThemeTab = () => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        Theme
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Upload, view, or update your dashboard theme. (PNG files only)
      </Typography>

      <Box
        onDrop={handleThemeDrop}
        onDragOver={handleThemeDragOver}
        sx={{
          border: '2px dashed',
          borderColor: themeFile ? 'success.main' : 'grey.400',
          borderRadius: 2,
          p: 4,
          mb: 3,
          textAlign: 'center',
          bgcolor: themeFile ? 'rgba(46, 125, 50, 0.08)' : 'rgba(0, 0, 0, 0.02)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'rgba(25, 118, 210, 0.08)',
          },
        }}
      >
        <input
          type="file"
          id="theme-file-input"
          style={{ display: 'none' }}
          accept=".png,image/png"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) {
              if (!file.type.includes('png') && !file.name.endsWith('.png')) {
                toast.error('Only PNG files are allowed for theme');
                return;
              }
              handleThemeUpload(e);
            }
          }}
        />
        <label htmlFor="theme-file-input" style={{ cursor: 'pointer', display: 'block' }}>
          <Upload sx={{ fontSize: 48, color: themeFile ? 'success.main' : 'grey.500', mb: 1 }} />
          <Typography variant="body1" color={themeFile ? 'success.main' : 'text.secondary'}>
            {themeFile ? themeFile.name : 'Drag & drop a PNG file here, or click to browse'}
          </Typography>
        </label>
      </Box>

      {/* Theme Preview after upload */}
      {themeFile && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Theme Preview
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={window.URL.createObjectURL(themeFile)}
              alt="Theme preview"
              style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, objectFit: 'contain' }}
            />
          </Box>
        </Paper>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<Upload />}
          onClick={uploadTheme}
          disabled={!themeFile || appearanceLoading}
        >
          {appearanceLoading ? 'Uploading...' : 'Upload Theme'}
        </Button>
      </Stack>

      {/* Theme List */}
      {themeLoading ? (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Uploaded Themes
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
            {[1, 2, 3].map((index) => (
              <Box key={index} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'grey.400' }}>
                <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2 }} />
              </Box>
            ))}
          </Box>
        </Box>
      ) : currentTheme && ((currentTheme.Templates && currentTheme.Templates.length > 0) || (Array.isArray(currentTheme) && currentTheme.length > 0)) ? (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Uploaded Themes
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
            {(currentTheme.Templates || currentTheme).map((theme) => (
              <Box key={theme.id} sx={{ p: 3, borderRadius: 2, position: 'relative', border: '1px solid', borderColor: 'grey.400', overflow: 'hidden' }}>
                <IconButton
                  sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                  onClick={(e) => handleThemeMenuOpen(e, theme)}
                >
                  <MoreVert />
                </IconButton>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, pr: 4 }}>
                  {theme.template_name}
                </Typography>
                {theme.template_url && (
                  <Box sx={{ 
                    overflowX: 'auto', 
                    whiteSpace: 'nowrap',
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-track': { bgcolor: 'grey.100', borderRadius: 3 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 },
                  }}>
                    <Box
                      component="button"
                      onClick={() => {
                        setSelectedTheme(theme);
                        setThemePreviewOpen(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedTheme(theme);
                          setThemePreviewOpen(true);
                        }
                      }}
                      sx={{
                        border: 'none',
                        background: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        width: '100%',
                        display: 'block',
                        '&:focus': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: 2,
                        },
                      }}
                      aria-label={`Preview ${theme.template_name}`}
                    >
                      <img
                        src={theme.template_url}
                        alt={theme.template_name}
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: 200, 
                          borderRadius: 8, 
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      {/* Theme Menu */}
      <Menu
        anchorEl={themeMenuAnchor}
        open={Boolean(themeMenuAnchor)}
        onClose={handleThemeMenuClose}
        PaperProps={{
          sx: {
            minWidth: 180,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            borderRadius: 2,
          }
        }}
      >
        <MenuItem
          onClick={() => {
            handleThemeMenuClose();
            if (selectedTheme?.template_url) {
              window.open(selectedTheme.template_url, '_blank');
            }
          }}
          sx={{ py: 1.5 }}
        >
          <ViewIcon sx={{ mr: 2, fontSize: 20, color: 'text.secondary' }} /> View
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleThemeMenuClose();
            if (selectedTheme && themeFile) {
              updateTheme(selectedTheme.id, selectedTheme.template_name);
            } else {
              toast.info('Select a theme file first to update');
            }
          }}
          sx={{ py: 1.5 }}
        >
          <Edit sx={{ mr: 2, fontSize: 20, color: 'text.secondary' }} /> Update
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedTheme) {
              deleteTheme(selectedTheme.id);
            }
          }}
          sx={{ py: 1.5 }}
        >
          <Delete sx={{ mr: 2, fontSize: 20, color: 'text.secondary' }} /> Delete
        </MenuItem>
      </Menu>

      {/* Theme Preview Dialog */}
      <Dialog open={themePreviewOpen} onClose={() => setThemePreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Theme Preview</DialogTitle>
        <DialogContent>
          {selectedTheme ? (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedTheme.template_name || 'Theme Preview'}
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 400, overflow: 'auto' }}>
                {selectedTheme.template_url ? (
                  <img
                    src={selectedTheme.template_url}
                    alt="Theme preview"
                    style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, objectFit: 'contain' }}
                  />
                ) : (
                  <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                    {JSON.stringify(selectedTheme, null, 2)}
                  </pre>
                )}
              </Paper>
            </Box>
          ) : currentTheme ? (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {currentTheme.name || currentTheme.template_name || 'Theme Preview'}
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 400, overflow: 'auto' }}>
                <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                  {JSON.stringify(currentTheme, null, 2)}
                </pre>
              </Paper>
            </Box>
          ) : (
            <Typography>No theme available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThemePreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const handleFontDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const validExtensions = ['.woff', '.woff2', '.ttf', '.otf'];
      const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      if (!hasValidExtension) {
        toast.error('Only font files (.woff, .woff2, .ttf, .otf) are allowed');
        return;
      }
      setFontFile(file);
      toast.success('Font file selected. Click "Save Font" to upload.');
    }
  };

  const handleFontDragOver = (e) => {
    e.preventDefault();
  };

  const renderFontTab = () => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        Font
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Upload, view, or update your dashboard font.
      </Typography>

      <Box
        onDrop={handleFontDrop}
        onDragOver={handleFontDragOver}
        sx={{
          border: '2px dashed',
          borderColor: fontFile ? 'success.main' : 'grey.400',
          borderRadius: 2,
          p: 4,
          mb: 3,
          textAlign: 'center',
          bgcolor: fontFile ? 'rgba(46, 125, 50, 0.08)' : 'rgba(0, 0, 0, 0.02)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'rgba(25, 118, 210, 0.08)',
          },
        }}
      >
        <input
          type="file"
          id="font-file-input"
          style={{ display: 'none' }}
          accept=".woff,.woff2,.ttf,.otf"
          onChange={handleFontUpload}
        />
        <label htmlFor="font-file-input" style={{ cursor: 'pointer', display: 'block' }}>
          <Upload sx={{ fontSize: 48, color: fontFile ? 'success.main' : 'grey.500', mb: 1 }} />
          <Typography variant="body1" color={fontFile ? 'success.main' : 'text.secondary'}>
            {fontFile ? fontFile.name : 'Drag & drop a font file here, or click to browse'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Supported formats: .woff, .woff2, .ttf, .otf
          </Typography>
        </label>
      </Box>

      {/* Font Preview after upload */}
      {fontFile && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Font Preview
          </Typography>
          <style>
            {`
              @font-face {
                font-family: 'PreviewFont';
                src: url(${window.URL.createObjectURL(fontFile)});
              }
            `}
          </style>
          <Typography sx={{ fontFamily: 'PreviewFont', fontSize: '24px', mb: 1 }}>
            The quick brown fox jumps over the lazy dog
          </Typography>
          <Typography sx={{ fontFamily: 'PreviewFont', fontSize: '18px', mb: 1 }}>
            ABCDEFGHIJKLMNOPQRSTUVWXYZ
          </Typography>
          <Typography sx={{ fontFamily: 'PreviewFont', fontSize: '18px' }}>
            abcdefghijklmnopqrstuvwxyz 0123456789
          </Typography>
        </Paper>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<Upload />}
          onClick={uploadFont}
          disabled={!fontFile || appearanceLoading}
        >
          {appearanceLoading ? 'Uploading...' : 'Upload Font'}
        </Button>
      </Stack>

      {/* Font List */}
      {currentFont && currentFont.Fonts && currentFont.Fonts.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Uploaded Fonts
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
            {currentFont.Fonts.map((font) => (
              <Box key={font.id} sx={{ p: 3, borderRadius: 2, position: 'relative', border: '1px solid', borderColor: 'grey.400', overflow: 'hidden' }}>
                <IconButton
                  sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                  onClick={(e) => handleFontMenuOpen(e, font)}
                >
                  <MoreVert />
                </IconButton>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, pr: 4 }}>
                  {font.font_name}
                </Typography>
                <style>
                  {`
                    @font-face {
                      font-family: 'Font-${font.id}';
                      src: url('${font.font_file_url}');
                    }
                  `}
                </style>
                <Box sx={{ 
                  overflowX: 'auto', 
                  whiteSpace: 'nowrap',
                  '&::-webkit-scrollbar': { height: 6 },
                  '&::-webkit-scrollbar-track': { bgcolor: 'grey.100', borderRadius: 3 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 },
                }}>
                  <Typography sx={{ fontFamily: `'Font-${font.id}'`, fontSize: '24px', mb: 1 }}>
                    The quick brown fox
                  </Typography>
                  <Typography sx={{ fontFamily: `'Font-${font.id}'`, fontSize: '18px', mb: 1 }}>
                    ABCDEFGHIJKLMNOPQRSTUVWXYZ
                  </Typography>
                  <Typography sx={{ fontFamily: `'Font-${font.id}'`, fontSize: '16px', color: 'text.secondary' }}>
                    abcdefghijklmnopqrstuvwxyz 0123456789
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Font Menu */}
      <Menu
        anchorEl={fontMenuAnchor}
        open={Boolean(fontMenuAnchor)}
        onClose={handleFontMenuClose}
        PaperProps={{
          sx: {
            minWidth: 180,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            borderRadius: 2,
          }
        }}
      >
        <MenuItem
          onClick={() => {
            handleFontMenuClose();
            if (selectedFont?.font_file_url) {
              window.open(selectedFont.font_file_url, '_blank');
            }
          }}
          sx={{ py: 1.5 }}
        >
          <Upload sx={{ mr: 2, fontSize: 20, color: 'text.secondary' }} /> Download
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleFontMenuClose();
            if (selectedFont && fontFile) {
              updateFont(selectedFont.id, selectedFont.font_name);
            } else {
              toast.info('Select a font file first to update');
            }
          }}
          sx={{ py: 1.5 }}
        >
          <Edit sx={{ mr: 2, fontSize: 20, color: 'text.secondary' }} /> Update
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedFont) {
              deleteFont(selectedFont.id);
            }
          }}
          sx={{ py: 1.5 }}
        >
          <Delete sx={{ mr: 2, fontSize: 20, color: 'text.secondary' }} /> Delete
        </MenuItem>
      </Menu>
    </Box>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 5 }}>
        <Typography variant="h4" gutterBottom>
          Settings
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            aria-label="settings tabs"
            sx={{
              minHeight: 'auto',
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTab-root': {
                minHeight: 'auto',
                minWidth: 'auto',
                padding: '8px 16px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 400,
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'text.primary',
                  fontWeight: 600,
                  borderBottom: '2px solid',
                  borderColor: 'text.primary',
                },
              },
            }}
          >
            <Tab label="Password" />
            <Tab label="Theme" />
            <Tab label="Font" />
          </Tabs>
        </Box>

        <Paper sx={{ p: 4, boxShadow: 2, borderRadius: 2 }}>
          {activeTab === 0 && renderSecurityTab()}
          {activeTab === 1 && renderThemeTab()}
          {activeTab === 2 && renderFontTab()}
        </Paper>
      </Box>
    </Container>
  );
};

export default SettingPage;
